/* global React */
/* ──────────────────────────────────────────────────────────────────────
   SolutionsHub — /solutions/  (Mirror layout)
   ──────────────────────────────────────────────────────────────────────
   Brand-true diptych: TwinMaq (navy-deep half) on the left, TwinMs
   (aqua-wash light half) on the right, separated by a vertical "spine"
   carrying BIM data glyphs and a pulsing handover dot.

   Below the split:
     ▸ Navy outcome band — "Sell it. Then run it." closing statement
     ▸ Light aqua-wash trust strip — 4 dark-navy pillar cards
       (card-inversion rule from the light-theme spec)

   Brand conventions strictly followed:
     ▸ Outfit font everywhere (no Playfair, no Bebas Neue)
     ▸ Navy + Aqua palette only (no terracotta, no warm gold)
     ▸ <em> = aqua color, font-style: normal (set by CSS)
     ▸ Title scale clamp(34px,4.6vw,64px), weight 700, tracking -0.025em
     ▸ Eyebrow 12px ALLCAPS w/ 0.18em tracking + leading hairline

   React-state hover-lean:
     Hovering either half makes the whole stage lean towards it (CSS grid
     template columns animate via the `.is-leaning--*` class). Aliased
     `useSolState` to avoid name collisions with other components on the
     page that destructure React.useState at module scope.
   ────────────────────────────────────────────────────────────────────── */

const { useState: useSolState, useEffect: useSolEffect, useRef: useSolRef } = React;
// Path-based AR localization for hardcoded labels (solutions content from content/solutions_ar.js).
function dzIsAr() { return typeof window !== 'undefined' && window.location.pathname.startsWith('/ar/'); }
function dzT(en, ar) { return dzIsAr() ? ar : en; }

/* ── useSolReveal — adds `.is-in-view` to a ref'd element when it scrolls
   into view. Used by sections that animate in on first reveal (outcome
   arc, questions cards, trust quote). Same observer pattern as the
   logos strip and lifecycle diagram, factored out for re-use. */
function useSolReveal(threshold) {
  const ref = useSolRef(null);
  useSolEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) el.classList.add('is-in-view'); },
      { threshold: threshold || 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

/* ── CountUp — increments a number from 0 → target when it scrolls into view.
   Same pattern used on /about/ and /projects/. Aliased hooks above to avoid
   collisions with other components on the same page. */
function SolCountUp({ target, suffix, duration }) {
  const ref       = useSolRef(null);
  const [val, setVal] = useSolState(0);
  const startedRef    = useSolRef(false);

  useSolEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting || startedRef.current) return;
      startedRef.current = true;
      const t0   = performance.now();
      const dur  = duration || 1400;
      const ease = (t) => 1 - Math.pow(1 - t, 3);
      function tick(t) {
        const p = Math.min(1, (t - t0) / dur);
        setVal(Math.round(target * ease(p)));
        if (p < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
      obs.disconnect();
    }, { threshold: 0.4 });
    obs.observe(node);
    return () => obs.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{val}{suffix || ''}</span>;
}

/* Inline icon set — small monoline glyphs, brand stroke weight 1.7 */
const SOL_ICONS = {
  building: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
      <path d="M3 21h18" />
      <path d="M9 8h2M13 8h2M9 12h2M13 12h2M9 16h2M13 16h2" />
    </svg>
  ),
  pin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s7-7.5 7-12a7 7 0 1 0-14 0c0 4.5 7 12 7 12z" />
      <circle cx="12" cy="9" r="2.4" />
    </svg>
  ),
  layers: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l9 5-9 5-9-5 9-5z" />
      <path d="M3 13l9 5 9-5" />
      <path d="M3 17l9 5 9-5" />
    </svg>
  ),
  camera: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  gauge: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 14l4-4" />
      <circle cx="12" cy="14" r="1.4" fill="currentColor" stroke="none" />
      <path d="M4 16a8 8 0 1 1 16 0" />
    </svg>
  ),
  sensor: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="2.2" />
      <path d="M8.5 8.5a5 5 0 0 0 0 7M15.5 15.5a5 5 0 0 0 0-7" />
      <path d="M5.5 5.5a9 9 0 0 0 0 13M18.5 18.5a9 9 0 0 0 0-13" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l8 3v6c0 4.5-3.4 8.4-8 9-4.6-.6-8-4.5-8-9V6l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  leaf: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 19c0-8 6-14 14-14 0 8-6 14-14 14z" />
      <path d="M5 19c4-4 7-7 11-9" />
    </svg>
  ),
  predict: (
    /* Predictive-maintenance icon: clock + checkmark — anticipating action */
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
      <path d="M16 16l1.6 1.6 3-3" />
    </svg>
  )
};

const SOL_ARROW = (
  <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M3 8h9M8 4l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.8"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── BIM Lifecycle diagram — the hero's visual centrepiece.
   ────────────────────────────────────────────────────────────────────
   Renders the 11 BIM lifecycle stages as a horizontal SVG timeline with
   two product markers pinned to specific stages (TwinMaq at Stage 02,
   TwinMs at Stage 09). The diagram tells the story the page is built on:
   "we cover all 11 stages, and TwinMs/TwinMaq hold the model at the two
   moments where information usually gets lost."

   Animations:
     ▸ The connecting line draws from left to right on scroll-into-view
       via stroke-dasharray + stroke-dashoffset
     ▸ Stage dots fade in sequentially (80ms stagger)
     ▸ The two product markers slide down from their labels with their
       identity colours and a continuous pulse glow
   ──────────────────────────────────────────────────────────────────── */
function SolutionsLifecycle({ data }) {
  const wrapRef = useSolRef(null);

  useSolEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) el.classList.add('is-in-view'); },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /* SVG geometry — single source of truth for spacing.
     ViewBox is 1100×260 (fits inside a max-width container, scales with width). */
  const VB_W = 1100;
  const VB_H = 260;
  const STAGES = data.stages;
  const N = STAGES.length;             /* 11 */
  const PAD_X = 50;                    /* horizontal padding so dots don't touch edges */
  const STEP = (VB_W - PAD_X * 2) / (N - 1);
  const LINE_Y = 200;                  /* y-pos of the timeline */
  const xFor = (i) => PAD_X + i * STEP;

  /* For dasharray draw-in animation we need the line length. */
  const LINE_LEN = VB_W - PAD_X * 2;

  return (
    <div ref={wrapRef} className="dz-sol-lifecycle">
      <svg
        className="dz-sol-lifecycle__svg"
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        aria-label="BIM lifecycle — 11 stages, two solutions"
      >
        {/* Gradient defs — line goes aqua → teal across the lifecycle */}
        <defs>
          <linearGradient id="dzSolLineGrad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%"  stopColor="#4FC8F4" stopOpacity="0.85" />
            <stop offset="50%" stopColor="#A8E1F4" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#4FB5A4" stopOpacity="0.85" />
          </linearGradient>
        </defs>

        {/* Connecting line — drawn left-to-right via stroke-dashoffset */}
        <line
          className="dz-sol-lifecycle__line"
          x1={PAD_X}        y1={LINE_Y}
          x2={VB_W - PAD_X} y2={LINE_Y}
          stroke="url(#dzSolLineGrad)"
          strokeWidth="1.5"
          strokeLinecap="round"
          style={{ strokeDasharray: LINE_LEN, strokeDashoffset: LINE_LEN }}
        />

        {/* Stage nodes (11 dots + numbers + labels).
            Each stage carries a data-priority so labels can be progressively
            dropped at narrower widths via media queries. The narrative
            anchors (start, both product positions, end) are "high" priority
            and stay visible at every desktop width. */}
        {STAGES.map((s, i) => {
          const x = xFor(i);
          const isAnchor = data.markers.some(m => m.stageIndex === i);
          /* Priority: 0=Programming high, 1=Concept high (TwinMaq anchor),
             2=Detailed low, 3=Analysis mid, 4=Docs low, 5=Fabrication mid,
             6=4D/5D low, 7=Construction mid, 8=Operation high (TwinMs anchor),
             9=Renovation low, 10=Demolition high. */
          const priority =
            (i === 0 || i === STAGES.length - 1 || isAnchor) ? 'high' :
            (i % 2 === 1)                                    ? 'mid'  :
            'low';
          return (
            <g
              key={s.num}
              className={'dz-sol-lifecycle__stage' + (isAnchor ? ' is-anchor' : '')}
              data-priority={priority}
              style={{ '--dz-sol-stage-delay': `${i * 80}ms` }}
            >
              <circle
                cx={x} cy={LINE_Y}
                r={isAnchor ? 6 : 3.5}
                className="dz-sol-lifecycle__dot"
              />
              <text
                x={x} y={LINE_Y + 28}
                textAnchor="middle"
                className="dz-sol-lifecycle__num"
              >{s.num}</text>
              <text
                x={x} y={LINE_Y + 46}
                textAnchor="middle"
                className="dz-sol-lifecycle__label"
              >{s.label}</text>
            </g>
          );
        })}

        {/* Product markers — drop-line + pill containing logo + name */}
        {data.markers.map((m) => {
          const x = xFor(m.stageIndex);
          return (
            <g
              key={m.name}
              className={'dz-sol-lifecycle__marker dz-sol-lifecycle__marker--' + m.side}
              style={{ '--dz-sol-stage-delay': `${(m.stageIndex * 80) + 200}ms` }}
            >
              {/* Drop line from below the marker pill down to the stage dot.
                  The pills now live in HTML above the SVG; this line is
                  drawn from y=20 (near the top of the SVG viewBox, just
                  below the pill bottom) down to the stage dot. */}
              <line
                x1={x} y1={LINE_Y - 8}
                x2={x} y2={20}
                strokeWidth="1.2"
                className="dz-sol-lifecycle__droplink"
              />
              {/* Pulse halo behind the dot */}
              <circle
                cx={x} cy={LINE_Y}
                r="14"
                className="dz-sol-lifecycle__halo"
              />
            </g>
          );
        })}
      </svg>

      {/* HTML pill labels for each marker (positioned absolutely above the SVG)
          rendered outside the SVG so we get text styling, image rendering,
          and hover affordances "for free". */}
      <div className="dz-sol-lifecycle__pills">
        {data.markers.map((m) => {
          const xPercent = ((PAD_X + m.stageIndex * STEP) / VB_W) * 100;
          /* The logo bitmap doesn't read on a dark pill; render the name as
             text instead, with the suffix wrapped in <em> so it picks up
             the identity colour (aqua/teal) automatically. */
          const baseName = m.name.replace(/(Maq|Ms)$/, '');
          const suffix   = m.name.match(/(Maq|Ms)$/)?.[0] || '';
          return (
            <a
              key={m.name}
              href="#sol-stage"
              className={'dz-sol-lifecycle__pill dz-sol-lifecycle__pill--' + m.side}
              style={{ '--dz-sol-pill-x': `${xPercent}%` }}
            >
              <span className="dz-sol-lifecycle__pill-stage">{m.stage}</span>
              <span className="dz-sol-lifecycle__pill-name">
                {baseName}<em>{suffix}</em>
              </span>
              {/* The customer question — moved here from the standalone
                  Questions section. The pill is now a complete narrative
                  unit: stage marker → product → real problem → scroll hint. */}
              {m.question ? (
                <span className="dz-sol-lifecycle__pill-q">{m.question}</span>
              ) : null}
              {m.hint ? (
                <span className="dz-sol-lifecycle__pill-hint">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M12 5v14M5 12l7 7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {m.hint}
                </span>
              ) : null}
            </a>
          );
        })}
      </div>
    </div>
  );
}

/* ── A single half of the split stage ──
   Hovering this half makes the stage lean towards it AND surfaces a
   reveal card (a real product UI screenshot + a stat) — so the lean
   serves a function instead of being decorative. */
function SolutionHalf({ data, onLean, onLeanEnd }) {
  return (
    <div
      className={'dz-sol-half dz-sol-half--' + data.side}
      onMouseEnter={() => onLean(data.side)}
      onMouseLeave={onLeanEnd}
      style={data.bgImage ? { '--dz-sol-bg': `url('${data.bgImage}')` } : null}
    >
      <div className="dz-sol-half__inner">
        <div>
          <span className="dz-sol-half__eyebrow">{data.eyebrow}</span>
          <h2 className="dz-sol-half__q">
            {data.questionLeading}
            <em>{data.questionAccent}</em>
            {data.questionTrailing}
          </h2>

          <div className="dz-sol-half__product">
            {data.productLeading}<em>{data.productAccent}</em>
            <span className="dz-sol-half__product-sub">{data.productSub}</span>
          </div>

          <p className="dz-sol-half__tagline">{data.tagline}</p>

          <ul className="dz-sol-half__caps" aria-label={data.productLeading + data.productAccent + ' capabilities'}>
            {data.capabilities.map((cap, i) => (
              <li key={i} className="dz-sol-half__cap">
                <span className="dz-sol-half__cap-ico">
                  {SOL_ICONS[cap.icon] || SOL_ICONS.gauge}
                </span>
                <span className="dz-sol-half__cap-label">{cap.label}</span>
              </li>
            ))}
          </ul>
        </div>

        {data.reveal ? (
          <aside className="dz-sol-half__reveal" aria-hidden="true">
            <div className="dz-sol-half__reveal-frame">
              <div className="dz-sol-half__reveal-bar">
                <span /><span /><span />
              </div>
              <img src={data.reveal.previewImg} alt={data.reveal.previewAlt} loading="lazy" />
            </div>
            <div className="dz-sol-half__reveal-status">
              <span className="dz-sol-half__reveal-head">{data.reveal.status.headline}</span>
              <span className="dz-sol-half__reveal-sub">{data.reveal.status.sub}</span>
            </div>
            <span className="dz-sol-half__reveal-tag">{data.reveal.tag}</span>
          </aside>
        ) : null}

        <a href={data.cta.href} className="dz-sol-half__cta">
          <span>{data.cta.label}</span>
          {SOL_ARROW}
        </a>
      </div>
    </div>
  );
}

/* ── The vertical spine: clean aqua→teal gradient line, travelling
       pulse, central handover halo, and rotated labels. The earlier
       BIM-glyph stream was visual noise at the rendered scale; this
       quieter treatment reads more confidently. ── */
function SolutionSpine() {
  return (
    <div className="dz-sol-spine" aria-hidden="true">
      <div className="dz-sol-spine__rail" />
      <div className="dz-sol-spine__handover" />
      <span className="dz-sol-spine__label">{dzT('Same BIM model', 'نموذج BIM ذاته')}</span>
      <span className="dz-sol-spine__label dz-sol-spine__label--bottom">{dzT('Carries through 50 yrs', 'يمتدّ عبر 50 عاماً')}</span>
    </div>
  );
}

/* ── Main component ── */
function SolutionsHub() {
  const data = window.DZ_SOLUTIONS;
  const [leanSide, setLeanSide] = useSolState(null);

  /* Sequential fade-in for the client logo strip on scroll-into-view —
     gives the strip a moment of "alive" without a continuous loop that
     would distract from the metrics + quote below it. */
  const logosRef    = useSolReveal(0.3);
  /* Scroll-reveals so the page has a consistent motion language as the
     user scrolls — the outcome arc draws itself, the trust quote
     slides up gently. (Questions ref removed — the questions now live
     inside the hero's lifecycle pills, no separate section to reveal.) */
  const outcomeRef = useSolReveal(0.35);
  const quoteRef   = useSolReveal(0.45);

  const stageClass =
    'dz-sol-stage' +
    (leanSide === 'maq' ? ' dz-sol-stage--lean-maq' : '') +
    (leanSide === 'ms'  ? ' dz-sol-stage--lean-ms'  : '');

  return (
    <main className="dz-solutions" id="dz-main" aria-label={data.sectionAriaLabel}>

      {/* ── Page opener: navy hero band ──
          Header (eyebrow + title + lead) sits centred above the BIM
          lifecycle diagram. The diagram is the visual centrepiece — the
          old logo watermarks are gone (the product markers ON the diagram
          replace them with a meaningful function). */}
      <section className="dz-sol-hero">
        <div className="dz-sol-hero__glow dz-sol-hero__glow--maq" aria-hidden="true" />
        <div className="dz-sol-hero__glow dz-sol-hero__glow--ms"  aria-hidden="true" />

        <div className="dz-sol-hero__inner">
          <header className="dz-sol-hero__head">
            <span className="dz-sol-hero__eyebrow">{data.hero.eyebrow}</span>
            <h1 className="dz-sol-hero__title">
              {data.hero.titleLeading}<em>{data.hero.titleAccent}</em>{data.hero.titleTrailing}
            </h1>
            <p className="dz-sol-hero__lead">{data.hero.lead}</p>
            {data.hero.cta && (
              <div className="dz-sol-hero__cta">
                <a className="dz-btn dz-btn--aqua" href={data.hero.cta.href}>{data.hero.cta.label}</a>
              </div>
            )}
          </header>

          {/* BIM lifecycle diagram — 11 stages with TwinMaq/TwinMs markers */}
          {data.hero.lifecycle ? (
            <SolutionsLifecycle data={data.hero.lifecycle} />
          ) : null}

          {data.hero.scrollHint ? (
            <a href="#sol-stage" className="dz-sol-hero__scroll" aria-label={data.hero.scrollHint}>
              <span>{data.hero.scrollHint}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                <path d="M12 5v14M5 12l7 7 7-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          ) : null}
        </div>
      </section>

      {/* (Standalone Questions section removed — the customer questions
          now live inside the lifecycle marker pills in the hero, where
          they're more directly tied to where each solution sits in the
          BIM lifecycle.) */}

      {/* ── The split stage (the answer to both questions, sat between the
          two light framing bands) ── */}
      <section id="sol-stage" className={stageClass}>
        <SolutionHalf
          data={data.solutions[0]}
          onLean={setLeanSide}
          onLeanEnd={() => setLeanSide(null)}
        />
        <SolutionSpine />
        <SolutionHalf
          data={data.solutions[1]}
          onLean={setLeanSide}
          onLeanEnd={() => setLeanSide(null)}
        />
      </section>

      {/* ── Outcome statement (navy band) ──
          Three additions over the previous version:
          (1) a huge ghosted "50" sitting behind the headline as a visual
              echo of the "operated for fifty years" promise;
          (2) a thin aqua→teal gradient arc beneath the headline — a
              literal rendering of "the BIM model carries through";
          (3) the trailing clause now wraps "BIM model" in <em> so it
              picks up the aqua accent (brand convention). */}
      <section ref={outcomeRef} className="dz-sol-outcome">
        <div className="dz-sol-outcome__inner">
          {data.outcome.ghostImage ? (
            <img
              src={data.outcome.ghostImage}
              alt={data.outcome.ghostAlt || ''}
              aria-hidden="true"
              className="dz-sol-outcome__ghost-img"
              loading="lazy"
            />
          ) : null}

          <div className="dz-sol-outcome__eye">{data.outcome.eyebrow}</div>
          <h2 className="dz-sol-outcome__title">
            <em className="dz-sol-outcome__accent--maq">{data.outcome.accentA}</em>
            {data.outcome.midText}
            <em className="dz-sol-outcome__accent--ms">{data.outcome.accentB}</em>
            {data.outcome.trailingAccent ? (
              <span className="dz-sol-outcome__trail">
                {data.outcome.trailingPre}
                <em>{data.outcome.trailingAccent}</em>
                {data.outcome.trailingPost}
              </span>
            ) : null}
          </h2>

          {/* Aqua → teal gradient arc connecting the two accent phrases.
              Visually says "the BIM model travels from sale to operation." */}
          <svg
            className="dz-sol-outcome__arc"
            viewBox="0 0 600 36"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="dzSolArcGrad" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%"  stopColor="#4FC8F4" stopOpacity="0.85" />
                <stop offset="50%" stopColor="#A8E1F4" stopOpacity="0.85" />
                <stop offset="100%" stopColor="#4FB5A4" stopOpacity="0.85" />
              </linearGradient>
            </defs>
            <path
              d="M 8,28 Q 300,2 592,28"
              fill="none"
              stroke="url(#dzSolArcGrad)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            {/* Two anchor dots — start (aqua/TwinMaq) and end (teal/TwinMs) */}
            <circle cx="8"   cy="28" r="3" fill="#4FC8F4"  />
            <circle cx="592" cy="28" r="3" fill="#4FB5A4"  />
          </svg>

          <p className="dz-sol-outcome__lead">{data.outcome.lead}</p>
        </div>
      </section>

      {/* ── Lower light band: REAL EVIDENCE ──
          Three-part proof block (replaces the previous generic-pillars
          treatment): client logo strip → metrics row (CountUp) → real
          attributed testimonial. Bookends the Mirror with the same
          --dz-aqua-wash bg as the Questions section above. */}
      <section className="dz-sol-trust" aria-label={data.evidence.eyebrow}>
        <div className="dz-sol-trust__inner">

          <header className="dz-sol-trust__head">
            <div className="dz-sol-trust__eye">{data.evidence.eyebrow}</div>
            <h2 className="dz-sol-trust__title">
              {data.evidence.titleLeading}<em>{data.evidence.titleAccent}</em>{data.evidence.titleTrailing}
            </h2>
          </header>

          {/* (1) Client logo strip — real Vision 2030 work.
                  Each <li> carries data-logo-key so per-logo sizing can be
                  targeted via attribute selectors in CSS (the brand marks
                  have very different aspect ratios). */}
          <ul ref={logosRef} className="dz-sol-trust__clients" aria-label="Clients">
            {data.evidence.clients.map((c) => (
              <li
                key={c.key}
                data-logo-key={c.key}
                className="dz-sol-trust__client"
              >
                <img src={c.src} alt={c.alt} loading="lazy" />
              </li>
            ))}
          </ul>

          {/* (2) Metrics row — CountUp on scroll */}
          <div className="dz-sol-trust__metrics">
            {data.evidence.metrics.map((m) => (
              <div key={m.label} className="dz-sol-trust__metric">
                <span className="dz-sol-trust__metric-num">
                  <SolCountUp target={m.target} suffix={m.suffix} />
                </span>
                <span className="dz-sol-trust__metric-lbl">{m.label}</span>
                <span className="dz-sol-trust__metric-sub">{m.sub}</span>
              </div>
            ))}
          </div>

          {/* (3) Real testimonial — attributed quote */}
          <figure ref={quoteRef} className="dz-sol-trust__quote">
            <span className="dz-sol-trust__quote-mark" aria-hidden="true">&ldquo;</span>
            <blockquote className="dz-sol-trust__quote-text">
              {data.evidence.quote.text}
            </blockquote>
            <figcaption className="dz-sol-trust__quote-attr">
              <span className="dz-sol-trust__quote-name">{data.evidence.quote.speakerName}</span>
              <span className="dz-sol-trust__quote-role">
                {data.evidence.quote.speakerRole} · {data.evidence.quote.speakerCompany}
              </span>
              {data.evidence.quote.speakerNote ? (
                <span className="dz-sol-trust__quote-note">{data.evidence.quote.speakerNote}</span>
              ) : null}
            </figcaption>
          </figure>

        </div>
      </section>

      {/* Closing CTA — leads into the unified form below the page */}
      <section className="dz-sol-closing">
        <div className="dz-sol-closing__inner">
          <p className="dz-sol-closing__line">{dzT('Ready to put one of these platforms to work?', 'جاهز لتشغيل إحدى هاتين المنصّتين؟')}</p>
          <a className="dz-sol-closing__btn" href="#inquiry">
            {dzT('Request a demo', 'اطلب عرضاً توضيحياً')}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      </section>

    </main>
  );
}

window.SolutionsHub = SolutionsHub;
